import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useNavigate } from 'react-router-dom';
import ActiveBadge from 'core/badges/ActiveBadge';
import IconButton from '@mui/material/IconButton';
import { useSelector, useDispatch } from 'react-redux';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';

import WebhooksActions from './WebhooksActions';
import ChipButton from 'core/buttons/ChipButton';
import FormCheckbox from 'core/forms/FormCheckbox';
import WebhookPagination from './WebhookPagination';
import TableContainer from 'core/table/TableContainer';
import VerifiedBadge from 'core/badges/VerifiedBadge';
import TanstackReactTable from 'core/table/TanstackReactTable';

import Auth from 'utils/Auth';
import app from 'app/config/app';
import { buildRoute } from 'routes';
import constants from 'app/config/constants';
import { useConfirm } from 'core/mui-confirm';
import Dimensions from 'app/config/Dimensions';
import useQueryParams from 'hooks/useQueryParams';
import { getWebhooks } from 'app/features/webhooks/webhookSaga';
import { deleteWebhook } from 'app/features/webhooks/webhookSaga';
import { getWebhookBriefDetailsInit } from 'app/features/webhooks/webhookSlice';
import isEmpty from 'helpers/isEmpty';
import { permissions } from 'app/data/permissions';
import { HasPermission } from 'hoc/withRole';

export default function Webhooks({ preFilterByPartner, partnerId }) {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const confirm = useConfirm();

  const webhooksState = useSelector((state) => state.webhooks.index);

  const { params, data, isLoading } = webhooksState;

  const [queryParams] = useQueryParams(app.ignoreFilterKeys);

  const [columnVisibility, setColumnVisibility] = useState({});

  /**
   * handle delete webhook
   *
   */
  const handleDelete = (webhookId) => {
    confirm({
      icon: 'warning',
      confirmationText: 'Yes, Delete it.',
      description: 'This will permanently delete this webhook.',
    })
      .then(() => dispatch(deleteWebhook(webhookId)))
      .catch(() => {});
  };

  const columns = useMemo(
    () => [
      {
        id: constants.TABLE_CHECKBOX_SELECT_CHECKBOX_ID,
        header: ({ table }) => (
          <FormCheckbox
            forTableHead={true}
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div>
            <FormCheckbox
              {...{
                checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: constants.TABLE_SERIAL_NUMBER_COLUMN_ID,
        header: 'SN',
        accessorFn: (row) => row.sn,
      },
      ...(Auth.isSuperAdmin()
        ? [
            {
              header: 'Partner Name',
              accessorFn: (row) => row.partner_name,
            },
          ]
        : []),
      {
        header: 'URL',
        accessorFn: (row) => row.url,
      },
      {
        header: 'Object Type',
        accessorFn: (row) => row.object_type,
      },
      {
        header: 'Is Active ?',
        accessorFn: (row) => row.is_active,
        cell: ({ row }) => <ActiveBadge isActive={!!row.original.is_active} />,
      },
      {
        header: 'Is Verified ?',
        accessorFn: (row) => row.is_verified,
        cell: ({ row }) => <VerifiedBadge isVerified={!!row.original.is_verified} />,
      },
      {
        id: constants.TABLE_ACTION_COLUMN_ID,
        header: 'Action',
        accessorFn: (row) => row.id,
        cell: ({ row }) => (
          <Box display="flex" gap={1}>
            <HasPermission permission={permissions.CREATE_WEBHOOK_HEADER}>
              <ChipButton
                onClick={() => {
                  navigate(
                    buildRoute(
                      'webhook-headers.create',
                      Auth.isSuperAdmin() ? { partner_id: row.original.partner_id } : {},
                    ),
                  );
                }}
              >
                Add Webhook Header
              </ChipButton>
            </HasPermission>
            <IconButton
              size={Dimensions.table.iconButton.size}
              onClick={() => {
                dispatch(
                  getWebhookBriefDetailsInit({
                    showBriefDetailModal: true,
                    webhookId: row.original.id,
                  }),
                );
              }}
            >
              <AspectRatioIcon fontSize={Dimensions.table.iconButton.iconSize} />
            </IconButton>
            <IconButton
              size={Dimensions.table.iconButton.size}
              onClick={() => navigate(buildRoute('webhooks.show', row.original.id))}
            >
              <FullscreenIcon fontSize={Dimensions.table.iconButton.iconSize} />
            </IconButton>
            <IconButton
              size={Dimensions.table.iconButton.size}
              color="error"
              onClick={() => handleDelete(row.original.id)}
              disabled={row.original?.isDeleting || false}
            >
              <DeleteForeverIcon fontSize={Dimensions.table.iconButton.iconSize} />
            </IconButton>
          </Box>
        ),
      },
    ],
    [JSON.stringify(data)],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    dispatch(
      getWebhooks({
        ...queryParams,
        ...params,
        ...(preFilterByPartner
          ? {
              partner_id: partnerId,
            }
          : {}),
      }),
    );
  }, [JSON.stringify(params)]);

  return (
    <TableContainer>
      {!preFilterByPartner && <WebhooksActions table={table} />}
      <TanstackReactTable
        table={table}
        data={data}
        isLoading={isLoading}
        columnsCount={
          columns.length - Object.entries(columnVisibility ?? {}).filter(([k, v]) => !v).length
        }
      />
      <WebhookPagination />
    </TableContainer>
  );
}

Webhooks.propTypes = {
  preFilterByPartner: PropTypes.bool,
  partnerId: (props, propName, componentName) => {
    if (props['preFilterByPartner'] === true && isEmpty(props[propName])) {
      return new Error(
        `${propName} is required when 'preFilterByPartner' props is 'true' in ${componentName} component`,
      );
    } else if (props['preFilterByPartner'] === true && typeof props[propName] !== 'string') {
      return new Error(`${propName} value must be string ${componentName} component`);
    }
  },
};
